export function shopifyFontToThemeFront(
  shopifyFontSetting: string,
): string | null {
  const pos = shopifyFontSetting.lastIndexOf('_');
  const familyId = shopifyFontSetting.substring(0, pos);
  const variantId = shopifyFontSetting.substring(pos + 1);
  const shopifyFont = shopifyFontMap[familyId];

  if (shopifyFont) {
    const variant = shopifyFont.variants[variantId];
    return `${shopifyFont.family}:${variant}`;
  }

  return null;
}

interface ShopifyFont {
  family: string;
  variants: Record<string, string | undefined>;
}

/**
 * Map of all available shopify fonts to their equivalent theme font.
 * @see https://shopify.dev/docs/themes/architecture/settings/fonts#available-fonts
 */
const shopifyFontMap: Record<string, ShopifyFont | undefined> = {
  abel: { family: 'Abel', variants: { n4: 'wght@400' } },
  abril_fatface: { family: 'Abril Fatface', variants: { n4: 'wght@400' } },
  alegreya: {
    family: 'Alegreya',
    variants: {
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n5: 'wght@500',
      i5: 'ital,wght@1,500',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
      n8: 'wght@800',
      i8: 'ital,wght@1,800',
      n9: 'wght@900',
      i9: 'ital,wght@1,900',
    },
  },
  alegreya_sans: {
    family: 'Alegreya Sans',
    variants: {
      n1: 'wght@100',
      i1: 'ital,wght@1,100',
      n3: 'wght@300',
      i3: 'ital,wght@1,300',
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n5: 'wght@500',
      i5: 'ital,wght@1,500',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
      n8: 'wght@800',
      i8: 'ital,wght@1,800',
      n9: 'wght@900',
      i9: 'ital,wght@1,900',
    },
  },
  amiri: {
    family: 'Amiri',
    variants: {
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
    },
  },
  anonymous_pro: {
    family: 'Anonymous Pro',
    variants: {
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
    },
  },
  arapey: {
    family: 'Arapey',
    variants: { n4: 'wght@400', i4: 'ital,wght@1,400' },
  },
  archivo: {
    family: 'Archivo',
    variants: {
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n5: 'wght@500',
      i5: 'ital,wght@1,500',
      n6: 'wght@600',
      i6: 'ital,wght@1,600',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
    },
  },
  archivo_narrow: {
    family: 'Archivo Narrow',
    variants: {
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n5: 'wght@500',
      i5: 'ital,wght@1,500',
      n6: 'wght@600',
      i6: 'ital,wght@1,600',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
    },
  },
  arimo: {
    family: 'Arimo',
    variants: {
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
    },
  },
  armata: { family: 'Armata', variants: { n4: 'wght@400' } },
  arvo: {
    family: 'Arvo',
    variants: {
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
    },
  },
  asap: {
    family: 'Asap',
    variants: {
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n5: 'wght@500',
      i5: 'ital,wght@1,500',
      n6: 'wght@600',
      i6: 'ital,wght@1,600',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
    },
  },
  assistant: {
    family: 'Assistant',
    variants: {
      n2: 'wght@200',
      n3: 'wght@300',
      n4: 'wght@400',
      n6: 'wght@600',
      n7: 'wght@700',
      n8: 'wght@800',
    },
  },
  asul: { family: 'Asul', variants: { n4: 'wght@400', n7: 'wght@700' } },
  bio_rhyme: {
    family: 'Bio Rhyme',
    variants: {
      n2: 'wght@200',
      n3: 'wght@300',
      n4: 'wght@400',
      n7: 'wght@700',
      n8: 'wght@800',
    },
  },
  bitter: {
    family: 'Bitter',
    variants: { n4: 'wght@400', i4: 'ital,wght@1,400', n7: 'wght@700' },
  },
  cabin: {
    family: 'Cabin',
    variants: {
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n5: 'wght@500',
      i5: 'ital,wght@1,500',
      n6: 'wght@600',
      i6: 'ital,wght@1,600',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
    },
  },
  cardo: {
    family: 'Cardo',
    variants: { n4: 'wght@400', i4: 'ital,wght@1,400', n7: 'wght@700' },
  },
  catamaran: {
    family: 'Catamaran',
    variants: {
      n1: 'wght@100',
      n2: 'wght@200',
      n3: 'wght@300',
      n4: 'wght@400',
      n5: 'wght@500',
      n6: 'wght@600',
      n7: 'wght@700',
      n8: 'wght@800',
      n9: 'wght@900',
    },
  },
  chivo: {
    family: 'Chivo',
    variants: {
      n3: 'wght@300',
      i3: 'ital,wght@1,300',
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
      n9: 'wght@900',
      i9: 'ital,wght@1,900',
    },
  },
  cormorant: {
    family: 'Cormorant',
    variants: {
      n3: 'wght@300',
      i3: 'ital,wght@1,300',
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n5: 'wght@500',
      i5: 'ital,wght@1,500',
      n6: 'wght@600',
      i6: 'ital,wght@1,600',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
    },
  },
  crimson_text: {
    family: 'Crimson Text',
    variants: {
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n6: 'wght@600',
      i6: 'ital,wght@1,600',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
    },
  },
  dm_sans: {
    family: 'DM Sans',
    variants: {
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n5: 'wght@500',
      i5: 'ital,wght@1,500',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
    },
  },
  domine: { family: 'Domine', variants: { n4: 'wght@400', n7: 'wght@700' } },
  dosis: {
    family: 'Dosis',
    variants: {
      n2: 'wght@200',
      n3: 'wght@300',
      n4: 'wght@400',
      n5: 'wght@500',
      n6: 'wght@600',
      n7: 'wght@700',
      n8: 'wght@800',
    },
  },
  eczar: {
    family: 'Eczar',
    variants: {
      n4: 'wght@400',
      n5: 'wght@500',
      n6: 'wght@600',
      n7: 'wght@700',
      n8: 'wght@800',
    },
  },
  fira_sans: {
    family: 'Fira Sans',
    variants: {
      n1: 'wght@100',
      i1: 'ital,wght@1,100',
      n2: 'wght@200',
      i2: 'ital,wght@1,200',
      n3: 'wght@300',
      i3: 'ital,wght@1,300',
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n5: 'wght@500',
      i5: 'ital,wght@1,500',
      n6: 'wght@600',
      i6: 'ital,wght@1,600',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
      n8: 'wght@800',
      i8: 'ital,wght@1,800',
      n9: 'wght@900',
      i9: 'ital,wght@1,900',
    },
  },
  fjalla_one: { family: 'Fjalla One', variants: { n4: 'wght@400' } },
  glegoo: { family: 'Glegoo', variants: { n4: 'wght@400', n7: 'wght@700' } },
  ibm_plex_sans: {
    family: 'IBM Plex Sans',
    variants: {
      n1: 'wght@100',
      i1: 'ital,wght@1,100',
      n2: 'wght@200',
      i2: 'ital,wght@1,200',
      n3: 'wght@300',
      i3: 'ital,wght@1,300',
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n5: 'wght@500',
      i5: 'ital,wght@1,500',
      n6: 'wght@600',
      i6: 'ital,wght@1,600',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
    },
  },
  inconsolata: {
    family: 'Inconsolata',
    variants: { n4: 'wght@400', n7: 'wght@700' },
  },
  inknut_antiqua: {
    family: 'Inknut Antiqua',
    variants: {
      n3: 'wght@300',
      n4: 'wght@400',
      n5: 'wght@500',
      n6: 'wght@600',
      n7: 'wght@700',
      n8: 'wght@800',
      n9: 'wght@900',
    },
  },
  inter: {
    family: 'Inter',
    variants: {
      n1: 'wght@100',
      n2: 'wght@200',
      n3: 'wght@300',
      n4: 'wght@400',
      n5: 'wght@500',
      n6: 'wght@600',
      n7: 'wght@700',
      n8: 'wght@800',
      n9: 'wght@900',
    },
  },
  josefin_sans: {
    family: 'Josefin Sans',
    variants: {
      n1: 'wght@100',
      i1: 'ital,wght@1,100',
      n3: 'wght@300',
      i3: 'ital,wght@1,300',
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n6: 'wght@600',
      i6: 'ital,wght@1,600',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
    },
  },
  josefin_slab: {
    family: 'Josefin Slab',
    variants: {
      n1: 'wght@100',
      i1: 'ital,wght@1,100',
      n3: 'wght@300',
      i3: 'ital,wght@1,300',
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n6: 'wght@600',
      i6: 'ital,wght@1,600',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
    },
  },
  kalam: {
    family: 'Kalam',
    variants: { n3: 'wght@300', n4: 'wght@400', n7: 'wght@700' },
  },
  karla: {
    family: 'Karla',
    variants: {
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
    },
  },
  kreon: {
    family: 'Kreon',
    variants: { n3: 'wght@300', n4: 'wght@400', n7: 'wght@700' },
  },
  lato: {
    family: 'Lato',
    variants: {
      n1: 'wght@100',
      i1: 'ital,wght@1,100',
      n2: 'wght@200',
      i2: 'ital,wght@1,200',
      n3: 'wght@300',
      i3: 'ital,wght@1,300',
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n5: 'wght@500',
      i5: 'ital,wght@1,500',
      n6: 'wght@600',
      i6: 'ital,wght@1,600',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
      n8: 'wght@800',
      i8: 'ital,wght@1,800',
      n9: 'wght@900',
      i9: 'ital,wght@1,900',
    },
  },
  libre_baskerville: {
    family: 'Libre Baskerville',
    variants: { n4: 'wght@400', i4: 'ital,wght@1,400', n7: 'wght@700' },
  },
  libre_franklin: {
    family: 'Libre Franklin',
    variants: {
      n1: 'wght@100',
      i1: 'ital,wght@1,100',
      n2: 'wght@200',
      i2: 'ital,wght@1,200',
      n3: 'wght@300',
      i3: 'ital,wght@1,300',
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n5: 'wght@500',
      i5: 'ital,wght@1,500',
      n6: 'wght@600',
      i6: 'ital,wght@1,600',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
      n8: 'wght@800',
      i8: 'ital,wght@1,800',
      n9: 'wght@900',
      i9: 'ital,wght@1,900',
    },
  },
  lobster: { family: 'Lobster', variants: { n4: 'wght@400' } },
  lobster_two: {
    family: 'Lobster Two',
    variants: {
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
    },
  },
  lora: {
    family: 'Lora',
    variants: {
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
    },
  },
  maven_pro: {
    family: 'Maven Pro',
    variants: {
      n4: 'wght@400',
      n5: 'wght@500',
      n7: 'wght@700',
      n9: 'wght@900',
    },
  },
  megrim: { family: 'Megrim', variants: { n4: 'wght@400' } },
  merriweather_sans: {
    family: 'Merriweather Sans',
    variants: {
      n3: 'wght@300',
      i3: 'ital,wght@1,300',
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
      n8: 'wght@800',
      i8: 'ital,wght@1,800',
    },
  },
  montserrat: {
    family: 'Montserrat',
    variants: {
      n1: 'wght@100',
      i1: 'ital,wght@1,100',
      n2: 'wght@200',
      i2: 'ital,wght@1,200',
      n3: 'wght@300',
      i3: 'ital,wght@1,300',
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n5: 'wght@500',
      i5: 'ital,wght@1,500',
      n6: 'wght@600',
      i6: 'ital,wght@1,600',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
      n8: 'wght@800',
      i8: 'ital,wght@1,800',
      n9: 'wght@900',
      i9: 'ital,wght@1,900',
    },
  },
  mouse_memoirs: { family: 'Mouse Memoirs', variants: { n4: 'wght@400' } },
  muli: {
    family: 'Muli',
    variants: {
      n2: 'wght@200',
      i2: 'ital,wght@1,200',
      n3: 'wght@300',
      i3: 'ital,wght@1,300',
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n6: 'wght@600',
      i6: 'ital,wght@1,600',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
      n8: 'wght@800',
      i8: 'ital,wght@1,800',
      n9: 'wght@900',
      i9: 'ital,wght@1,900',
    },
  },
  neuton: {
    family: 'Neuton',
    variants: {
      n2: 'wght@200',
      n3: 'wght@300',
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n7: 'wght@700',
      n8: 'wght@800',
    },
  },
  news_cycle: {
    family: 'News Cycle',
    variants: { n4: 'wght@400', n7: 'wght@700' },
  },
  newsreader: {
    family: 'Newsreader',
    variants: {
      n2: 'wght@200',
      i2: 'ital,wght@1,200',
      n3: 'wght@300',
      i3: 'ital,wght@1,300',
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n5: 'wght@500',
      i5: 'ital,wght@1,500',
      n6: 'wght@600',
      i6: 'ital,wght@1,600',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
      n8: 'wght@800',
      i8: 'ital,wght@1,800',
    },
  },
  nobile: {
    family: 'Nobile',
    variants: {
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n5: 'wght@500',
      i5: 'ital,wght@1,500',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
    },
  },
  noticia_text: {
    family: 'Noticia Text',
    variants: {
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
    },
  },
  noto_serif: {
    family: 'Noto Serif',
    variants: {
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
    },
  },
  nunito: {
    family: 'Nunito',
    variants: {
      n2: 'wght@200',
      i2: 'ital,wght@1,200',
      n3: 'wght@300',
      i3: 'ital,wght@1,300',
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n6: 'wght@600',
      i6: 'ital,wght@1,600',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
      n8: 'wght@800',
      i8: 'ital,wght@1,800',
      n9: 'wght@900',
      i9: 'ital,wght@1,900',
    },
  },
  nunito_sans: {
    family: 'Nunito Sans',
    variants: {
      n2: 'wght@200',
      i2: 'ital,wght@1,200',
      n3: 'wght@300',
      i3: 'ital,wght@1,300',
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n6: 'wght@600',
      i6: 'ital,wght@1,600',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
      n8: 'wght@800',
      i8: 'ital,wght@1,800',
      n9: 'wght@900',
      i9: 'ital,wght@1,900',
    },
  },
  old_standard_tt: {
    family: 'Old Standard TT',
    variants: { n4: 'wght@400', i4: 'ital,wght@1,400', n7: 'wght@700' },
  },
  open_sans: {
    family: 'Open Sans',
    variants: {
      n3: 'wght@300',
      i3: 'ital,wght@1,300',
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n6: 'wght@600',
      i6: 'ital,wght@1,600',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
      n8: 'wght@800',
      i8: 'ital,wght@1,800',
    },
  },
  open_sans_condensed: {
    family: 'Open Sans Condensed',
    variants: { n3: 'wght@300', i3: 'ital,wght@1,300', n7: 'wght@700' },
  },
  oswald: {
    family: 'Oswald',
    variants: {
      n2: 'wght@200',
      n3: 'wght@300',
      n4: 'wght@400',
      n5: 'wght@500',
      n6: 'wght@600',
      n7: 'wght@700',
    },
  },
  ovo: { family: 'Ovo', variants: { n4: 'wght@400' } },
  oxygen: {
    family: 'Oxygen',
    variants: { n3: 'wght@300', n4: 'wght@400', n7: 'wght@700' },
  },
  pt_mono: { family: 'PT Mono', variants: { n4: 'wght@400' } },
  pt_sans: {
    family: 'PT Sans',
    variants: {
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
    },
  },
  pt_sans_narrow: {
    family: 'PT Sans Narrow',
    variants: { n4: 'wght@400', n7: 'wght@700' },
  },
  pt_serif: {
    family: 'PT Serif',
    variants: {
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
    },
  },
  pacifico: { family: 'Pacifico', variants: { n4: 'wght@400' } },
  playball: { family: 'Playball', variants: { n4: 'wght@400' } },
  playfair_display: {
    family: 'Playfair Display',
    variants: {
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
      n9: 'wght@900',
      i9: 'ital,wght@1,900',
    },
  },
  poppins: {
    family: 'Poppins',
    variants: {
      n1: 'wght@100',
      i1: 'ital,wght@1,100',
      n2: 'wght@200',
      i2: 'ital,wght@1,200',
      n3: 'wght@300',
      i3: 'ital,wght@1,300',
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n5: 'wght@500',
      i5: 'ital,wght@1,500',
      n6: 'wght@600',
      i6: 'ital,wght@1,600',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
      n8: 'wght@800',
      i8: 'ital,wght@1,800',
      n9: 'wght@900',
      i9: 'ital,wght@1,900',
    },
  },
  prata: { family: 'Prata', variants: { n4: 'wght@400' } },
  prompt: {
    family: 'Prompt',
    variants: {
      n1: 'wght@100',
      i1: 'ital,wght@1,100',
      n2: 'wght@200',
      i2: 'ital,wght@1,200',
      n3: 'wght@300',
      i3: 'ital,wght@1,300',
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n5: 'wght@500',
      i5: 'ital,wght@1,500',
      n6: 'wght@600',
      i6: 'ital,wght@1,600',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
      n8: 'wght@800',
      i8: 'ital,wght@1,800',
      n9: 'wght@900',
      i9: 'ital,wght@1,900',
    },
  },
  proza_libre: {
    family: 'Proza Libre',
    variants: {
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n5: 'wght@500',
      i5: 'ital,wght@1,500',
      n6: 'wght@600',
      i6: 'ital,wght@1,600',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
      n8: 'wght@800',
      i8: 'ital,wght@1,800',
    },
  },
  quantico: {
    family: 'Quantico',
    variants: {
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
    },
  },
  quattrocento: {
    family: 'Quattrocento',
    variants: { n4: 'wght@400', n7: 'wght@700' },
  },
  quattrocento_sans: {
    family: 'Quattrocento Sans',
    variants: {
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
    },
  },
  questrial: { family: 'Questrial', variants: { n4: 'wght@400' } },
  quicksand: {
    family: 'Quicksand',
    variants: {
      n3: 'wght@300',
      n4: 'wght@400',
      n5: 'wght@500',
      n7: 'wght@700',
    },
  },
  rajdhani: {
    family: 'Rajdhani',
    variants: {
      n3: 'wght@300',
      n4: 'wght@400',
      n5: 'wght@500',
      n6: 'wght@600',
      n7: 'wght@700',
    },
  },
  raleway: {
    family: 'Raleway',
    variants: {
      n1: 'wght@100',
      i1: 'ital,wght@1,100',
      n2: 'wght@200',
      i2: 'ital,wght@1,200',
      n3: 'wght@300',
      i3: 'ital,wght@1,300',
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n5: 'wght@500',
      i5: 'ital,wght@1,500',
      n6: 'wght@600',
      i6: 'ital,wght@1,600',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
      n8: 'wght@800',
      i8: 'ital,wght@1,800',
      n9: 'wght@900',
      i9: 'ital,wght@1,900',
    },
  },
  righteous: { family: 'Righteous', variants: { n4: 'wght@400' } },
  roboto: {
    family: 'Roboto',
    variants: {
      n1: 'wght@100',
      i1: 'ital,wght@1,100',
      n3: 'wght@300',
      i3: 'ital,wght@1,300',
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n5: 'wght@500',
      i5: 'ital,wght@1,500',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
      n9: 'wght@900',
      i9: 'ital,wght@1,900',
    },
  },
  roboto_condensed: {
    family: 'Roboto Condensed',
    variants: {
      n3: 'wght@300',
      i3: 'ital,wght@1,300',
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
    },
  },
  roboto_mono: {
    family: 'Roboto Mono',
    variants: {
      n1: 'wght@100',
      i1: 'ital,wght@1,100',
      n3: 'wght@300',
      i3: 'ital,wght@1,300',
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n5: 'wght@500',
      i5: 'ital,wght@1,500',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
    },
  },
  roboto_slab: {
    family: 'Roboto Slab',
    variants: {
      n1: 'wght@100',
      n3: 'wght@300',
      n4: 'wght@400',
      n7: 'wght@700',
    },
  },
  rubik: {
    family: 'Rubik',
    variants: {
      n3: 'wght@300',
      i3: 'ital,wght@1,300',
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n5: 'wght@500',
      i5: 'ital,wght@1,500',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
      n9: 'wght@900',
      i9: 'ital,wght@1,900',
    },
  },
  shadows_into_light: {
    family: 'Shadows Into Light',
    variants: { n4: 'wght@400' },
  },
  slabo_13px: { family: 'Slabo 13px', variants: { n4: 'wght@400' } },
  smooch: { family: 'Smooch', variants: { n4: 'wght@400' } },
  source_code_pro: {
    family: 'Source Code Pro',
    variants: {
      n2: 'wght@200',
      n3: 'wght@300',
      n4: 'wght@400',
      n5: 'wght@500',
      n6: 'wght@600',
      n7: 'wght@700',
      n9: 'wght@900',
    },
  },
  source_sans_pro: {
    family: 'Source Sans Pro',
    variants: {
      n2: 'wght@200',
      i2: 'ital,wght@1,200',
      n3: 'wght@300',
      i3: 'ital,wght@1,300',
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n6: 'wght@600',
      i6: 'ital,wght@1,600',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
      n9: 'wght@900',
      i9: 'ital,wght@1,900',
    },
  },
  space_mono: {
    family: 'Space Mono',
    variants: {
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
    },
  },
  syne: {
    family: 'Syne',
    variants: {
      n4: 'wght@400',
      n5: 'wght@500',
      n6: 'wght@600',
      n7: 'wght@700',
      n8: 'wght@800',
    },
  },
  tenor_sans: { family: 'Tenor Sans', variants: { n4: 'wght@400' } },
  tinos: {
    family: 'Tinos',
    variants: {
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
    },
  },
  titillium_web: {
    family: 'Titillium Web',
    variants: {
      n2: 'wght@200',
      i2: 'ital,wght@1,200',
      n3: 'wght@300',
      i3: 'ital,wght@1,300',
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n6: 'wght@600',
      i6: 'ital,wght@1,600',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
      n9: 'wght@900',
    },
  },
  ubuntu: {
    family: 'Ubuntu',
    variants: {
      n3: 'wght@300',
      i3: 'ital,wght@1,300',
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n5: 'wght@500',
      i5: 'ital,wght@1,500',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
    },
  },
  unica_one: { family: 'Unica One', variants: { n4: 'wght@400' } },
  unna: {
    family: 'Unna',
    variants: {
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
    },
  },
  varela: { family: 'Varela', variants: { n4: 'wght@400' } },
  varela_round: { family: 'Varela Round', variants: { n4: 'wght@400' } },
  vidaloka: { family: 'Vidaloka', variants: { n4: 'wght@400' } },
  volkhov: {
    family: 'Volkhov',
    variants: {
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
    },
  },
  vollkorn: {
    family: 'Vollkorn',
    variants: {
      n4: 'wght@400',
      i4: 'ital,wght@1,400',
      n6: 'wght@600',
      i6: 'ital,wght@1,600',
      n7: 'wght@700',
      i7: 'ital,wght@1,700',
      n9: 'wght@900',
      i9: 'ital,wght@1,900',
    },
  },
  work_sans: {
    family: 'Work Sans',
    variants: {
      n1: 'wght@100',
      n2: 'wght@200',
      n3: 'wght@300',
      n4: 'wght@400',
      n5: 'wght@500',
      n6: 'wght@600',
      n7: 'wght@700',
      n8: 'wght@800',
      n9: 'wght@900',
    },
  },
  biz_udpmincho: {
    family: 'BIZ UDPMincho',
    variants: { n4: 'wght@400', n7: 'wght@700' },
  },
  dela_gothic_one: { family: 'Dela Gothic One', variants: { n4: 'wght@400' } },
  hina_mincho: { family: 'Hina Mincho', variants: { n4: 'wght@400' } },
  kaisei_decol: {
    family: 'Kaisei Decol',
    variants: { n4: 'wght@400', n5: 'wght@500', n7: 'wght@700' },
  },
  kaisei_opti: {
    family: 'Kaisei Opti',
    variants: { n4: 'wght@400', n5: 'wght@500', n7: 'wght@700' },
  },
  kaisei_tokumin: {
    family: 'Kaisei Tokumin',
    variants: {
      n4: 'wght@400',
      n5: 'wght@500',
      n7: 'wght@700',
      n8: 'wght@800',
    },
  },
  m_plus_1: {
    family: 'M PLUS 1',
    variants: {
      n1: 'wght@100',
      n2: 'wght@200',
      n3: 'wght@300',
      n4: 'wght@400',
      n5: 'wght@500',
      n6: 'wght@600',
      n7: 'wght@700',
      n8: 'wght@800',
      n9: 'wght@900',
    },
  },
  m_plus_rounded_1c: {
    family: 'M PLUS Rounded 1c',
    variants: {
      n1: 'wght@100',
      n3: 'wght@300',
      n4: 'wght@400',
      n5: 'wght@500',
      n7: 'wght@700',
      n8: 'wght@800',
      n9: 'wght@900',
    },
  },
  mochiy_pop_p_one: {
    family: 'Mochiy Pop P One',
    variants: { n4: 'wght@400' },
  },
  murecho: {
    family: 'Murecho',
    variants: {
      n1: 'wght@100',
      n2: 'wght@200',
      n3: 'wght@300',
      n4: 'wght@400',
      n5: 'wght@500',
      n6: 'wght@600',
      n7: 'wght@700',
      n8: 'wght@800',
      n9: 'wght@900',
    },
  },
  noto_sans_japanese: {
    family: 'Noto Sans Japanese',
    variants: {
      n1: 'wght@100',
      n2: 'wght@200',
      n3: 'wght@300',
      n4: 'wght@400',
      n5: 'wght@500',
      n6: 'wght@600',
      n7: 'wght@700',
      n8: 'wght@800',
      n9: 'wght@900',
    },
  },
  noto_serif_japanese: {
    family: 'Noto Serif Japanese',
    variants: {
      n2: 'wght@200',
      n3: 'wght@300',
      n4: 'wght@400',
      n5: 'wght@500',
      n6: 'wght@600',
      n7: 'wght@700',
      n9: 'wght@900',
    },
  },
  shippori_mincho: {
    family: 'Shippori Mincho',
    variants: {
      n4: 'wght@400',
      n5: 'wght@500',
      n6: 'wght@600',
      n7: 'wght@700',
      n8: 'wght@800',
    },
  },
  zen_kaku_gothic_new: {
    family: 'Zen Kaku Gothic New',
    variants: {
      n3: 'wght@300',
      n4: 'wght@400',
      n5: 'wght@500',
      n7: 'wght@700',
      n9: 'wght@900',
    },
  },
  zen_kurenaido: { family: 'Zen Kurenaido', variants: { n4: 'wght@400' } },
  zen_maru_gothic: {
    family: 'Zen Maru Gothic',
    variants: {
      n3: 'wght@300',
      n4: 'wght@400',
      n5: 'wght@500',
      n7: 'wght@700',
      n9: 'wght@900',
    },
  },
  zen_old_mincho: {
    family: 'Zen Old Mincho',
    variants: {
      n4: 'wght@400',
      n5: 'wght@500',
      n6: 'wght@600',
      n7: 'wght@700',
      n9: 'wght@900',
    },
  },
};
